import { CancelToken } from "axios";
import { ApiAuth, ApiCall } from "./api";
import { TPremiseResponse } from "./interface/IDefaults";

export type TSearchSource = "companies" | "certificates" | "messages";

export interface ISearchItem {
  id: string;
  title: string;
  subtitle?: string;
  path: string;
}

export interface ISearchGroup {
  source: TSearchSource;
  label: string;
  items: ISearchItem[];
  /** True quando existem mais resultados do que o teto devolvido pelo backend. */
  hasMore: boolean;
}

export interface ISearchResult {
  query: string;
  groups: ISearchGroup[];
}

/**
 * Card do Kanban 2.0 devolvido por `/kanban2/search` (formato do módulo, não do /search) —
 * espelho de `Kanban2Service.searchCards` no backend. O identificador vem como `cardId`, não `id`.
 */
export interface IKanbanSearchCard {
  cardId: string;
  title: string;
  seqCode?: string | null;
  boardName?: string;
  boardId?: string;
}

/**
 * Busca global (GEN-3497) — empresas, certidões e Caixa Postal.
 *
 * O Kanban 2.0 responde por um endpoint próprio (abaixo) porque a visibilidade de card
 * (board privado, escopo por empresa/persona) vive lá dentro. Duplicar aquela regra num
 * segundo lugar é como se vaza título de card privado.
 */
export const globalSearch = async (
  guid_client: string,
  q: string,
  limit = 5,
  cancelToken?: CancelToken
): Promise<TPremiseResponse<ISearchResult>> => {
  const url = `/search/${guid_client}?q=${encodeURIComponent(q)}&limit=${limit}`;
  return await new ApiCall(url, ApiAuth(cancelToken)).get<ISearchResult>();
};

/**
 * Busca de cards do Kanban 2.0 — o endpoint que já existe e aplica a visibilidade do módulo.
 *
 * GEN-3857: o cliente vai no header `x-client-guid`, como em toda chamada ao módulo (ver
 * `kanban2.service.ts`). Sem ele o backend resolve o PRIMEIRO cliente da conta, e quem estava com
 * outro cliente ativo — contas com vários clientes — buscava no cliente errado e via "Nada encontrado".
 */
export const searchKanbanCards = async (
  guid_client: string,
  q: string
): Promise<TPremiseResponse<IKanbanSearchCard[]>> => {
  const url = `/kanban2/search?q=${encodeURIComponent(q)}`;
  return await new ApiCall(url, ApiAuth({ "x-client-guid": guid_client })).get<IKanbanSearchCard[]>();
};
